import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { clockModeVisualConfigAtom, componentThemeAtom } from '../../../../atoms/v3/clockModeAtoms';

interface ComponentThemeProviderProps {
  children: React.ReactNode;
}

/**
 * 组件主题类型
 */
export type ComponentTheme = 'light' | 'dark';

/**
 * 组件主题提供者
 * 独立于时钟主题的组件主题系统，只支持黑白两色
 */
export const ComponentThemeProvider: React.FC<ComponentThemeProviderProps> = ({ children }) => {
  const [componentTheme] = useAtom(componentThemeAtom);
  const [themeConfig] = useAtom(clockModeVisualConfigAtom);

  useEffect(() => {
    // 移除所有组件主题类
    const componentThemeClasses = [
      'clock-component-theme-light',
      'clock-component-theme-dark',
      'clock-component-theme-glass',
    ];
    
    document.body.classList.remove(...componentThemeClasses);
    
    // 添加当前组件主题类
    document.body.classList.add(`clock-component-theme-${componentTheme}`);
    if (themeConfig.componentMaterial === 'glass') {
      document.body.classList.add('clock-component-theme-glass');
    }
    
    // 清理函数
    return () => {
      document.body.classList.remove(...componentThemeClasses);
    };
  }, [componentTheme, themeConfig.componentMaterial]);

  return <>{children}</>;
};

/**
 * 获取组件主题显示名称
 */
export const getComponentThemeDisplayName = (theme: ComponentTheme): string => {
  const themeNames: Record<ComponentTheme, string> = {
    light: '浅色组件',
    dark: '深色组件'
  };

  return themeNames[theme] || theme;
};

/**
 * 获取组件主题图标
 */
export const getComponentThemeIcon = (theme: ComponentTheme): React.ReactNode => {
  switch (theme) {
    case 'light':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="white"/>
        </svg>
      );
    case 'dark':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="black"/>
        </svg>
      );
    default:
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
  }
};

/**
 * 组件主题选择器
 */
interface ComponentThemeSelectorProps {
  currentTheme: ComponentTheme;
  onThemeChange: (theme: ComponentTheme) => void;
  className?: string;
}

export const ComponentThemeSelector: React.FC<ComponentThemeSelectorProps> = ({ 
  currentTheme, 
  onThemeChange, 
  className = '' 
}) => {
  const themes: ComponentTheme[] = ['light', 'dark'];

  return (
    <div className={`flex space-x-2 ${className}`}>
      {themes.map((theme) => (
        <button
          key={theme}
          onClick={() => onThemeChange(theme)}
          className={`
            p-2 rounded-lg border-2 transition-all duration-200
            flex items-center justify-center
            ${currentTheme === theme 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }
          `}
          title={getComponentThemeDisplayName(theme)}
        >
          {getComponentThemeIcon(theme)}
        </button>
      ))}
    </div>
  );
};

export default ComponentThemeProvider;
